import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Check, 
  ArrowRight, 
  Eye, 
  Users, 
  Calendar,
  Sparkles,
  Shield,
  Zap,
  Target,
  PenTool,
  DollarSign,
  BarChart3,
  MessageCircle,
  ChevronDown,
  ChevronUp,
  Play
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";
import { cubeLogo, landscapeVideos, shortsVideos } from "../data/mock";

// Video Testimonial Component (Landscape)
const VideoTestimonial = ({ video, index }) => {
  const [playing, setPlaying] = useState(false);
  const thumbUrl = `https://img.youtube.com/vi/${video.youtubeId}/maxresdefault.jpg`;
  const embedParams = "rel=0&modestbranding=1&showinfo=0&iv_load_policy=3&controls=1&fs=1&cc_load_policy=0&disablekb=0&autoplay=1";

  if (playing) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: index * 0.1 }}
        className="relative aspect-video overflow-hidden rounded-xl bg-black border border-white/10"
      >
        <iframe
          src={`https://www.youtube.com/embed/${video.youtubeId}?autoplay=1&${embedParams}`}
          title={video.title}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full absolute inset-0"
          loading="lazy"
        />
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className="relative aspect-video overflow-hidden rounded-xl cursor-pointer group bg-black border border-white/10 hover:border-red-500/30 transition-all"
      onClick={() => setPlaying(true)}
    >
      <img
        src={thumbUrl}
        alt={video.title}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 opacity-80 group-hover:opacity-100"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/20" />
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.div
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          className="w-16 h-16 rounded-full bg-[#dc2626] flex items-center justify-center shadow-lg shadow-[#dc2626]/30"
        >
          <Play className="w-7 h-7 text-white fill-white ml-1" />
        </motion.div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <p className="text-white font-medium text-sm line-clamp-2">{video.title}</p>
      </div>
    </motion.div>
  );
};

// Shorts Video Testimonial Component (Portrait)
const ShortsVideoTestimonial = ({ video, index }) => {
  const [playing, setPlaying] = useState(false);
  const thumbUrl = `https://img.youtube.com/vi/${video.youtubeId}/0.jpg`;
  const embedParams = "rel=0&modestbranding=1&showinfo=0&iv_load_policy=3&controls=1&fs=1&cc_load_policy=0&disablekb=0&autoplay=1";

  if (playing) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: index * 0.05 }}
        className="relative aspect-[9/16] overflow-hidden rounded-xl bg-black border border-white/10"
      >
        <iframe
          src={`https://www.youtube.com/embed/${video.youtubeId}?autoplay=1&${embedParams}`}
          title={video.title}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full absolute inset-0"
          loading="lazy"
        />
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.05 }}
      className="relative aspect-[9/16] overflow-hidden rounded-xl cursor-pointer group bg-black border border-white/10 hover:border-red-500/30 transition-all"
      onClick={() => setPlaying(true)}
    >
      <img
        src={thumbUrl}
        alt={video.title}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 opacity-80 group-hover:opacity-100"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20" />
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.div
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          className="w-12 h-12 rounded-full bg-[#dc2626] flex items-center justify-center shadow-lg shadow-[#dc2626]/30"
        >
          <Play className="w-5 h-5 text-white fill-white ml-0.5" />
        </motion.div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-3">
        <p className="text-white font-medium text-xs line-clamp-2">{video.title}</p>
        <p className="text-red-400 text-[10px] mt-1">{video.industry}</p>
      </div>
    </motion.div>
  );
};

// Pricing Plans Data
const pricingPlans = [
  {
    name: "Starter",
    price: "4,499",
    duration: "10 Days",
    views: "45,000+",
    leads: "75-90",
    recommended: false,
    features: [
      "Ad Design Included",
      "Facebook & Instagram Ads",
      "Budget Allocation",
      "Campaign Setup",
      "Basic Reporting",
    ]
  },
  {
    name: "Business",
    price: "8,499",
    duration: "20 Days",
    views: "75,000+",
    leads: "125-180",
    recommended: true,
    features: [
      "Ad Design Included",
      "Facebook & Instagram Ads",
      "Budget Allocation",
      "Campaign Setup",
      "Performance Optimization",
      "Weekly Reporting",
    ]
  },
  {
    name: "Grow",
    price: "13,499",
    duration: "30 Days",
    views: "96,000+",
    leads: "190-260",
    recommended: false,
    features: [
      "Premium Ad Design",
      "Facebook & Instagram Ads",
      "Advanced Targeting",
      "Campaign Setup & Management",
      "A/B Testing",
      "Daily Optimization",
      "Detailed Analytics Report",
    ]
  }
];

// Custom Plan Tiers
const customTiers = [
  { adCost: "< ₹5,000", fee: "12%" },
  { adCost: "₹5,000 - ₹25,000", fee: "8%" },
  { adCost: "₹25,000+", fee: "6%" },
];

// What's Included
const includedFeatures = [
  { icon: PenTool, title: "Designing Cost", desc: "Professional ad creatives included" },
  { icon: DollarSign, title: "Advertising Cost", desc: "Meta platform ad spend covered" },
  { icon: Zap, title: "Service Fee", desc: "Complete management & optimization" },
  { icon: Shield, title: "GST Included", desc: "No additional taxes" },
];

// FAQ Data
const faqData = [
  {
    question: "What platforms do you run ads on?",
    answer: "We run ads on Facebook and Instagram through Meta's advertising platform. These platforms offer the best reach and targeting options for most businesses."
  },
  {
    question: "Are the views and leads guaranteed?",
    answer: "The views and leads mentioned are estimated results based on our experience with similar campaigns. Actual results may vary depending on your industry, target audience, and ad content quality."
  },
  {
    question: "What's included in the designing cost?",
    answer: "We create professional ad creatives including images, carousel ads, and video content as needed. The number of creatives depends on your plan - higher plans include more creative variations for A/B testing."
  },
  {
    question: "Can I choose the custom plan instead?",
    answer: "Absolutely! The custom plan is perfect if you have a specific budget in mind. You set the advertising cost, and we charge a service fee percentage that decreases as your ad spend increases."
  },
  {
    question: "How do I track my campaign performance?",
    answer: "We provide regular reports based on your plan - basic reporting for Starter, weekly for Business, and detailed daily analytics for Grow. You'll have full visibility into views, clicks, leads, and ROI."
  },
  {
    question: "What if I want to pause or modify my campaign?",
    answer: "You can request modifications or pauses at any time. Our team will work with you to optimize the campaign based on performance data."
  }
];

const WHATSAPP_URL = "https://wa.me/918777624646?text=Hi%2C%20I%27m%20interested%20in%20the%20Super%20Ads%20Plan";

export default function SuperAdsPage() {
  const [openFaq, setOpenFaq] = useState(null);
  const [videoTab, setVideoTab] = useState("landscape");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleGetStarted = (planName) => {
    // Open Razorpay payment link
    window.open("https://razorpay.me/@cubemultimedia", "_blank");
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a]/90 backdrop-blur-lg border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/">
            <img src={cubeLogo} alt="Cube Multimedia" className="h-12 md:h-16" />
          </Link>
          <Button 
            onClick={() => window.open(WHATSAPP_URL, "_blank")}
            className="bg-[#dc2626] hover:bg-[#b91c1c] text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            Get Started
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-28 pb-16 md:pt-36 md:pb-24 px-4 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#dc2626]/20 rounded-full blur-[100px]" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#dc2626]/10 rounded-full blur-[120px]" />
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 bg-[#dc2626]/10 border border-[#dc2626]/30 rounded-full px-4 py-2 mb-6"
          >
            <Sparkles className="w-4 h-4 text-[#dc2626]" />
            <span className="text-sm text-[#dc2626] font-medium">All-Inclusive Advertising Plans</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight mb-6"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Cube Multimedia's
            <br />
            <span className="text-[#dc2626]">Super Ads Plan</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg md:text-xl text-[#a1a1aa] max-w-2xl mx-auto mb-8"
          >
            We manage everything from ad design, platform selection, and budget allocation to deliver optimal results for your campaign
          </motion.p>

          {/* Platform Icons */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="flex justify-center gap-6 mb-8"
          >
            <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-full">
              <svg viewBox="0 0 36 36" className="w-6 h-6 fill-[#1877F2]">
                <path d="M36 18c0-9.941-8.059-18-18-18S0 8.059 0 18c0 8.984 6.582 16.431 15.188 17.784V23.21h-4.57V18h4.57v-3.969c0-4.512 2.687-7.003 6.797-7.003 1.97 0 4.028.352 4.028.352v4.43h-2.27c-2.235 0-2.93 1.387-2.93 2.81V18h4.992l-.798 5.21h-4.194v12.574C29.418 34.431 36 26.984 36 18"/>
              </svg>
              <span className="text-white text-sm">Facebook</span>
            </div>
            <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-full">
              <svg viewBox="0 0 24 24" className="w-6 h-6">
                <defs>
                  <linearGradient id="ig-grad-sa" x1="0%" y1="100%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#FFDC80"/>
                    <stop offset="50%" stopColor="#F56040"/>
                    <stop offset="100%" stopColor="#C13584"/>
                  </linearGradient>
                </defs>
                <path fill="url(#ig-grad-sa)" d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
              <span className="text-white text-sm">Instagram</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-12 px-4 border-y border-white/[0.06]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-center text-xl md:text-2xl font-bold mb-8 text-white">
            Plans Include <span className="text-[#dc2626]">Everything</span> — No Hidden Costs
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {includedFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-4"
              >
                <div className="w-12 h-12 rounded-xl bg-[#dc2626]/10 flex items-center justify-center mx-auto mb-3">
                  <feature.icon className="w-6 h-6 text-[#dc2626]" />
                </div>
                <h3 className="font-semibold text-white mb-1">{feature.title}</h3>
                <p className="text-xs text-[#71717a]">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-16 md:py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Choose Your <span className="text-[#dc2626]">Advertising Plan</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`relative rounded-2xl p-6 md:p-8 ${
                  plan.recommended 
                    ? 'bg-gradient-to-b from-[#dc2626]/20 to-[#dc2626]/5 border-2 border-[#dc2626]/50' 
                    : 'bg-white/[0.02] border border-white/[0.06]'
                }`}
              >
                {plan.recommended && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#dc2626] text-white text-xs font-bold px-4 py-1 rounded-full">
                    RECOMMENDED
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl md:text-5xl font-bold text-white">₹{plan.price}</span>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-2 mb-6 p-4 bg-black/20 rounded-xl">
                  <div className="text-center">
                    <Calendar className="w-5 h-5 text-[#dc2626] mx-auto mb-1" />
                    <p className="text-white font-semibold text-sm">{plan.duration}</p>
                    <p className="text-[#71717a] text-xs">Duration</p>
                  </div>
                  <div className="text-center">
                    <Eye className="w-5 h-5 text-[#dc2626] mx-auto mb-1" />
                    <p className="text-white font-semibold text-sm">{plan.views}</p>
                    <p className="text-[#71717a] text-xs">Views*</p>
                  </div>
                  <div className="text-center">
                    <Users className="w-5 h-5 text-[#dc2626] mx-auto mb-1" />
                    <p className="text-white font-semibold text-sm">{plan.leads}</p>
                    <p className="text-[#71717a] text-xs">Leads*</p>
                  </div>
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3 text-[#a1a1aa] text-sm">
                      <Check className="w-4 h-4 text-green-500 shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <Button 
                  onClick={() => handleGetStarted(plan.name)}
                  className={`w-full py-4 rounded-xl font-semibold ${
                    plan.recommended 
                      ? 'bg-[#dc2626] hover:bg-[#b91c1c] text-white' 
                      : 'bg-white/10 hover:bg-white/20 text-white'
                  }`}
                >
                  Get Started <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </motion.div>
            ))}
          </div>

          <p className="text-center text-[#71717a] text-sm mt-6">*Estimated results based on campaign performance</p>
        </div>
      </section>

      {/* Custom Plan */}
      <section className="py-16 md:py-24 px-4 bg-gradient-to-b from-transparent via-[#dc2626]/5 to-transparent">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Or Select A <span className="text-[#dc2626]">Customized Plan</span>
            </h2>
            <p className="text-[#a1a1aa] text-lg max-w-2xl mx-auto">
              Create your own package that suits your business needs. Customize your goals, platforms, and budget according to your specific requirements.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-6 md:p-8"
          >
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              {customTiers.map((tier, index) => (
                <div 
                  key={index}
                  className="bg-black/20 rounded-xl p-5 text-center hover:bg-black/30 transition-colors"
                >
                  <p className="text-[#a1a1aa] text-sm mb-2">Advertising Cost</p>
                  <p className="text-xl font-bold text-white mb-3">{tier.adCost}</p>
                  <div className="border-t border-white/10 pt-3">
                    <p className="text-[#a1a1aa] text-sm mb-1">Service Fee</p>
                    <p className="text-3xl font-bold text-[#dc2626]">{tier.fee}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center">
              <p className="text-[#71717a] text-sm mb-4">
                Higher ad spend = Lower service fee percentage
              </p>
              <Button 
                onClick={() => window.open(WHATSAPP_URL, "_blank")}
                className="bg-[#dc2626] hover:bg-[#b91c1c] text-white px-8 py-4 rounded-xl font-semibold"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Discuss Custom Plan
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24 px-4">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Why Choose <span className="text-[#dc2626]">Super Ads?</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Target, title: "Expert Targeting", desc: "We reach your ideal customers with precision targeting on Meta platforms" },
              { icon: PenTool, title: "Professional Creatives", desc: "Eye-catching ad designs that convert viewers into customers" },
              { icon: BarChart3, title: "Performance Tracking", desc: "Real-time monitoring and optimization for maximum ROI" },
              { icon: Shield, title: "No Hidden Costs", desc: "Everything included - design, ads, service fee & GST" },
              { icon: Zap, title: "Quick Setup", desc: "Get your campaigns running within 24-48 hours" },
              { icon: Users, title: "Dedicated Support", desc: "Personal account manager for your campaigns" },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-6 hover:border-[#dc2626]/30 transition-colors"
              >
                <div className="w-12 h-12 rounded-xl bg-[#dc2626]/10 flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-[#dc2626]" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-[#71717a] text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Testimonials Section */}
      <section className="py-16 md:py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                What Our <span className="text-[#dc2626]">Clients Say</span>
              </h2>
              <p className="text-[#a1a1aa] text-lg">Real video feedback from businesses we've helped grow</p>
            </motion.div>
          </div>

          {/* Video Tabs */}
          <div className="flex justify-center gap-2 mb-8">
            <button
              onClick={() => setVideoTab("landscape")}
              className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                videoTab === "landscape"
                  ? "bg-[#dc2626] text-white"
                  : "bg-white/5 text-gray-400 hover:text-white hover:bg-white/10 border border-white/10"
              }`}
              data-testid="superads-video-tab-landscape"
            >
              Video Feedback
            </button>
            <button
              onClick={() => setVideoTab("shorts")}
              className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                videoTab === "shorts"
                  ? "bg-[#dc2626] text-white"
                  : "bg-white/5 text-gray-400 hover:text-white hover:bg-white/10 border border-white/10"
              }`}
              data-testid="superads-video-tab-shorts"
            >
              Shorts
            </button>
          </div>

          {/* Landscape Videos */}
          {videoTab === "landscape" && (
            <div className="grid md:grid-cols-2 gap-6">
              {landscapeVideos.filter(v => v.type === "feedback").map((video, index) => (
                <VideoTestimonial key={video.id} video={video} index={index} />
              ))}
            </div>
          )}

          {/* Shorts Videos */}
          {videoTab === "shorts" && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {shortsVideos.slice(0, 10).map((video, index) => (
                <ShortsVideoTestimonial key={video.id} video={video} index={index} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 px-4 bg-gradient-to-b from-transparent via-[#dc2626]/5 to-transparent">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Frequently Asked <span className="text-[#dc2626]">Questions</span>
            </h2>
          </motion.div>

          <div className="space-y-3">
            {faqData.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left"
                >
                  <span className="font-medium text-white pr-4">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-[#dc2626] shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-[#71717a] shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-5 text-[#a1a1aa]">
                    {faq.answer}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Ready to <span className="text-[#dc2626]">Grow Your Business?</span>
            </h2>
            <p className="text-[#a1a1aa] text-lg mb-8">
              Let's discuss which plan works best for you. Get in touch with our team today.
            </p>
            <Button 
              onClick={() => window.open(WHATSAPP_URL, "_blank")}
              className="bg-[#dc2626] hover:bg-[#b91c1c] text-white px-10 py-6 rounded-xl text-lg font-bold"
            >
              <MessageCircle className="w-6 h-6 mr-2" />
              Chat on WhatsApp
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-white/[0.06]">
        <div className="max-w-4xl mx-auto text-center">
          <Link to="/">
            <img src={cubeLogo} alt="Cube Multimedia" className="h-10 mx-auto mb-4" />
          </Link>
          <p className="text-[#52525b] text-sm">
            © {new Date().getFullYear()} Cube Multimedia. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
